import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import ContentData from "../../../constants/contentFile.json";
import { validateField } from "../AddProduct/AddProductView";
import { updateBatteryApi, updateInverterApi } from "../../../api/Api";
import { handleApiError } from "../../../constants/Constants";

export const EditProductContext = createContext({});
export const EditProductContextProvider = ({ children }) => {
  const [formData, setFormData] = useState({
    model: "",
    power: "",
    powerInVoltAmperes: "",
    volt: "",
    type: "",
    capacity: "",
    connectivity: "yes",
    solarModal: "yes",
    productLink: "",
    siteOrgin: "vguard.com",
  });
  const [errors, setErrors] = useState({
    model: "",
    power: "",
    powerInVoltAmperes: "",
    volt: "",
    type: "",
    capacity: "",
    connectivity: "",
    solarModal: "",
    productLink: "",
    siteOrgin: "",
  });
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [imgError, setImgError] = useState("");
  const [successAlertShow, setSuccessAlertShow] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const currentTabValue = localStorage.getItem("tabValue");
  const navigate = useNavigate();
  const location = useLocation();
  const [statusValue, setStatusValue] = useState(0);
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const ProductDetails = location?.state?.productData;
  const currentPage = location?.state?.page;
  const tabValue = localStorage.getItem("tabValue");
  useEffect(() => {
    if (ProductDetails) {
      setFormData((prevData) => ({
        model: ProductDetails.modelName || prevData.model,
        power: ProductDetails.power || prevData.power,
        powerInVoltAmperes:
          ProductDetails.powerInVoltAmperes || prevData.powerInVoltAmperes,
        volt: ProductDetails.batteryVoltage || prevData.volt,
        type: ProductDetails.type || prevData.type,
        capacity: ProductDetails.capacity || prevData.capacity,
        connectivity:
          ProductDetails.connectivity === true
            ? "Yes"
            : "No" || prevData.connectivity,
        solarModal:
          ProductDetails.solarModel === true
            ? "Yes"
            : "No" || prevData.solarModal,
        productLink: ProductDetails.url || prevData.productLink,
        siteOrgin:
          ProductDetails.siteOrigin == "com"
            ? "vguard.com"
            : "vguard.in" || prevData.siteOrgin,
      }));

      if (ProductDetails.image) {
        setFileUrl(ProductDetails?.image);
        setSelectedFile(handleExistingImageUrl(ProductDetails.image));
      }
    }
  }, [ProductDetails]);

  const convertImageUrlToFile = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.split("/").pop();
      const file = new File([blob], filename, { type: blob.type });
      return file;
    } catch (error) {
      return null;
    }
  };

  // Modified function to handle the image URL
  const handleExistingImageUrl = async (url) => {
    if (url && url.startsWith("http")) {
      const file = await convertImageUrlToFile(url);
      if (file) {
        setSelectedFile(file);
        setFileUrl(url);
        setImgError("");
        return file;
      }
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Fields to validate based on product type
    const commonFields = ["model", "productLink", "siteOrgin"];
    const inverterFields = [
      "power",
      "volt",
      "type",
      "connectivity",
      "solarModal",
    ];
    const batteryFields = ["capacity"];

    const fieldsToValidate = [
      ...commonFields,
      ...(currentTabValue === "1" ? inverterFields : batteryFields),
    ];

    fieldsToValidate?.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        isValid = false;
        newErrors[field] = error;
      }
    });

    // Only validate image if a new one is being uploaded
    if (selectedFile) {
      if (selectedFile.size > 2 * 1024 * 1024) {
        isValid = false;
        setImgError("File size exceeds 2MB limit");
      }
    }

    // Validate image
    if (!selectedFile) {
      isValid = false;
      setImgError("Product image is required");
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue =
      type === "radio" ? (checked ? value : formData[name]) : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    // Validate field on change
    const error = validateField(name, newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const editProduct = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setSpinnerStatus(true);

      try {
        // FormData to send the image as binary
        const formDataToSend = new FormData();
        formDataToSend.append("modelName", formData?.model);
        formDataToSend.append(
          "siteOrigin",
          formData.siteOrgin === "vguard.com" ? "com" : "in"
        );
        formDataToSend.append("url", formData.productLink);

        if (tabValue === "1") {
          formDataToSend.append("power", formData.power);
          formDataToSend.append("powerInVoltAmperes", formData.powerInVoltAmperes);
          formDataToSend.append("batteryVoltage", formData.volt);
          formDataToSend.append("type", formData.type);
          formDataToSend.append(
            "connectivity",
            formData.connectivity === "Yes" ? true : false
          );
          formDataToSend.append(
            "solarModel",
            formData.solarModal === "Yes" ? true : false
          );
        } else {
          formDataToSend.append("capacity", formData.capacity);
        }
        if (selectedFile) {
          formDataToSend.append("image", selectedFile);
        }

        // API based on tab selection
        const response =
          tabValue == "1"
            ? await updateInverterApi(ProductDetails?._id, formDataToSend)
            : await updateBatteryApi(ProductDetails?._id, formDataToSend);

        if (response.data.statusCode === 200) {
          setSpinnerStatus(false);
          setSuccessAlertShow(true);
          setSuccessAlertMessage(response?.data?.message);
          setErrorPopup(false);
        }
      } catch (error) {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
      setSpinnerStatus(false);
    }
  };

  const onImageChange = (file) => {
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setImgError("File size exceeds 2MB limit");
        clearImage();
        return;
      }

      setSelectedFile(file);
      const tempUrl = URL.createObjectURL(file);
      setFileUrl(tempUrl);
      setImgError("");
    }
  };

  const clearImage = () => {
    setSelectedFile(null);
    if (fileUrl && fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setImgError("");
  };

  const goToProductList = () => {
    navigate(RouteConstants.LIST_PRODUCTS, { state: { page: currentPage } });
  };
  const goToProductDetails = () => {
    navigate(RouteConstants.PRODUCT_DETAILS, {
      state: { id: ProductDetails?._id, page: currentPage },
    });
  };
  const goToLogin = () => {
    navigate(RouteConstants.LOGIN);
  };
  const providerValue = {
    spinnerStatus,
    goToProductList,
    formData,
    handleChange,
    fileUrl,
    onImageChange,
    clearImage,
    imgError,
    selectedFile,
    tabValue,
    spinnerStatus,
    currentTabValue,
    successAlertMessage,
    successAlertShow,
    editProduct,
    errors,
    goToProductDetails,
    errorPopup,
    errorPopupMessage,
    setErrorPopup,
    goToLogin,
    buttonText,
    statusValue,
  };
  return (
    <EditProductContext.Provider value={providerValue}>
      {children}
    </EditProductContext.Provider>
  );
};
